import { ComponentInternalInstance, getCurrentInstance, reactive, ref, Ref, toRef } from '@vue/composition-api';
import _ from 'lodash';

import CustomDialog from '@/renderer/scripts/utils/CustomDialog';
import { i18n_t } from '@/locales/i18n';
import smh, { SMH } from '@/renderer/scripts/smh';
import tips from '@/renderer/scripts/utils/tips';
import store from '@/renderer/store';
import { checkRoleIsAdmin } from '@/renderer/scripts/utils/UserRole';

export default function handler(params: {
  spaceType: Ref<'team' | 'group' | 'other'>;
  defaultRoleId?: Ref<number | undefined>;
  allowInactiveUser?: Ref<boolean>;
  originAddedMembers: Ref<CoFile.TeamOrMemberItem[]>;
  getTeamMemberTree(params: { teamId: number | string }): Promise<SMH.TeamMemberItemData[]>;
}) {
  const vm = (getCurrentInstance() as ComponentInternalInstance).proxy;
  const { spaceType, allowInactiveUser = ref(false), originAddedMembers = ref([]), defaultRoleId } = params;

  // 已添加的用户
  const addedMembers = ref<CoFile.TeamOrMemberItem[]>([]);
  const originAddedMembersSet = new Set<number | string>([]);
  const addedMembersSet = new Set<number | string>([]);

  const methods = {
    initOriginAdded(originAddedMembers: CoFile.TeamOrMemberItem[]) {
      const ownerRoleId = store.getters.ownerRole?.id;
      // 先清空 再更新
      methods.resetData();
      addedMembers.value = _.cloneDeep(originAddedMembers)
        .map(item => {
          if (item.userId) {
            addedMembersSet.add(item.userId);
            originAddedMembersSet.add(item.userId);
          }

          let type = 'authorize';
          if (item.isForbidden) {
            type = 'forbidden';
          } else if (item.tag === 1 || item.tag === 2 || (item.tag === 0 && item.inheritRoleId)) {
            type = 'inherit';
          }
          const uid = `${item.name}_${item.userId || item.id}`;
          return {
            ...item,
            type,
            uid,
          } as CoFile.TeamOrMemberItem;
        })
        .sort((a, b) => {
          // 所有者权限排最前面
          if (a.roleId !== ownerRoleId && b.roleId === ownerRoleId) return 1;
          // 团队排在用户前面
          if (a.spaceId) {
            if (a.spaceId && !b.spaceId) return -1;
          }
          // 默认权限成员默认排在前面
          if (a.isDefault && a.tag === 1) return -1;
          // 带标签权限排在不带标签之前
          if (a.tag !== 0 && b.tag === 0) return -1;
          return 0;
        });
    },
    addTeamOrMember(item: SMH.TeamTreeNode | SMH.UserBaseInfo, noDisableRoleSelect = true) {
      // FIX: 开启了禁用管理员选择角色
      if (!noDisableRoleSelect && checkRoleIsAdmin((item as SMH.UserBaseInfo).role) && spaceType.value !== 'team')
        return;

      // 未激活用户是否允许操作
      if (item.inactive && !allowInactiveUser.value) return;

      methods._addTeamOrMember(item);
    },
    _addTeamOrMember(item: SMH.TeamTreeNode | SMH.UserBaseInfo) {
      if ((item as SMH.TeamTreeNode).spaceId) {
        // 点击团队加号，团队成员为0时直接返回
        if (!(item as SMH.TeamTreeNode).userCount) {
          tips.error(i18n_t('当前团队没有可添加的成员'));
          return;
        }

        methods.addTeam(item as SMH.TeamTreeNode);
      } else {
        methods.addMember(item);
      }
    },
    /** 选择团队后查询里面的成员并添加 */
    async addTeam(item: SMH.TeamTreeNode) {
      if ((item.userCount as number) > 1000) {
        tips.error(i18n_t('一次最多可选择1000人，已到达上限'));
        return;
      }
      let memberList = [];
      // 已经加载过子节点就直接取
      if (item.children && item.children?.length > 0) {
        memberList = item.children.filter((child: any) => child.userId);
      } else {
        memberList = await params.getTeamMemberTree({ teamId: item.id });
        memberList = memberList.map((member: any) => ({
          ...member,
          name: member.nickname,
          userId: member.id,
        }));
      }
      // 不允许操作未激活用户时，需要过滤这些用户
      if (!allowInactiveUser.value) {
        memberList = memberList.filter(member => !member.inactive);
      }
      if (memberList.length === 0) {
        tips.error(i18n_t('当前团队没有可添加的成员'));
      }
      // 过滤已存在的用户后添加
      memberList.forEach(member => {
        if (!addedMembersSet.has(member.userId)) {
          methods.addMember(member);
        }
      });
    },
    addMember(item: SMH.TeamTreeNode | SMH.UserBaseInfo) {
      const newAddList = addedMembers.value.filter(item => item.newAdd);
      if (newAddList.length >= 1000) {
        tips.error(i18n_t('一次最多可选择1000人，已到达上限'));
        return;
      }
      const permisionItem: CoFile.TeamOrMemberItem = {
        roleId: i18n_t('设置权限'),
        roleName: '',
        role: '',
        name: '',
        checked: false,
        tag: 0,
        avatar: '',
        inactive: item.inactive,
        isTemporary: !!item.isTemporary,
        uid: '',
      };
      if ((item as SMH.TeamTreeNode).spaceId) {
        const teamItem = item as SMH.TeamTreeNode;
        permisionItem.spaceId = teamItem.spaceId;
        permisionItem.name = teamItem.name;
        permisionItem.id = teamItem.id;
        permisionItem.userCount = teamItem.userCount;
        permisionItem.uid = `${teamItem.name}_${teamItem.id}`;
      } else {
        const userItem = item as SMH.UserBaseInfo & { name: string; userId: number };
        permisionItem.userId = userItem.userId;
        permisionItem.name = userItem.name;
        permisionItem.countryCode = userItem.countryCode;
        permisionItem.phoneNumber = userItem.phoneNumber;
        permisionItem.accountName = userItem.accountName;
        permisionItem.role = userItem.role;
        permisionItem.avatar = userItem.avatar;
        permisionItem.uid = `${userItem.name}_${userItem.userId}`;
      }
      // 团队管理新增成员时权限使用该团队的默认权限
      if (defaultRoleId?.value) {
        permisionItem.roleId = +defaultRoleId.value;
      }
      const { editionFlag } = store.getters;
      // 管理员默认操作者权限
      // add: 团队空间中比较特别，不存在操作者，所以跳过，不做任何特殊处理。
      if (checkRoleIsAdmin((item as SMH.UserBaseInfo).role, true) && spaceType.value !== 'group') {
        // 团队版的管理员 默认最高[编辑者]
        // 企业版的管理员 默认最高[操作者]
        const operatorRoleId = store.getters?.operatorRole?.id; // 操作者
        const editorRoleId = store.getters?.editorRole?.id; // 编辑者
        permisionItem.roleId = editionFlag === 'team' ? editorRoleId : operatorRoleId;
      }

      // 授权列表内有相同项则不允许添加
      if (permisionItem.userId && addedMembersSet.has(permisionItem.userId)) {
        tips.error(i18n_t('该团队或成员已添加至列表'));
        return;
      }
      permisionItem.newAdd = true;
      permisionItem.type = 'authorize';
      addedMembers.value.push(permisionItem);
      permisionItem.userId && addedMembersSet.add(permisionItem.userId);
      if (!vm.$props.simpleMode) {
        tips.info(i18n_t('已添加至成员列表'));
      }
    },
    removeConfirm(fn: Function) {
      const dialog = CustomDialog.confirm({
        header: i18n_t('提示'),
        body: i18n_t('确定要删除吗？'),
        theme: 'warning',
        onConfirm: () => {
          if (dialog.hide) {
            dialog.hide();
          }
          fn();
        },
      });
    },
    /**
     * 若有userId，则按照userId删除
     * 若无userId，则按照调用removeTeamOrMember根据index删除
     */
    removeTeamOrMember(index: number, member: CoFile.TeamOrMemberItem) {
      // 若没有传递userId 则按照index进行删除
      if (member === undefined) {
        const [item] = addedMembers.value.splice(index, 1);
        if (item.userId) {
          addedMembersSet.delete(item.userId);
        }
        return;
      }
      // 过滤掉个人
      const splitByPhoneNumber = (item: CoFile.TeamOrMemberItem) => item.userId !== member?.userId;
      // 过滤掉团队
      const splitBySpaceId = (item: CoFile.TeamOrMemberItem) => item.spaceId !== member?.spaceId;
      // 由于permissionList 和renderPermissionList顺序不一致，所以按照index直接删除是不合理的，
      // 故使用userId做为唯一的标识进行区分删除
      addedMembers.value = addedMembers.value.filter((item: CoFile.TeamOrMemberItem) =>
        member.userId ? splitByPhoneNumber(item) : splitBySpaceId(item),
      );
      if (member.userId) {
        addedMembersSet.delete(member.userId);
      }
    },
    removeTeamOrMembers() {
      // 批量移除时需要从后往前移除，否则 index 序号不正确
      for (let i = addedMembers.value.length - 1; i >= 0; i--) {
        const item = addedMembers.value[i];
        item.checked && methods.removeTeamOrMember(i, item);
      }
    },
    changeTeamOrMembersPermission(roleId: number, noDisableRoleSelect = true) {
      if (roleId) {
        addedMembers.value.forEach(item => {
          const isUser = !checkRoleIsAdmin(item.role as string);
          if (item.checked && (isUser || noDisableRoleSelect)) {
            item.roleId = roleId;
          }
        });
      }
    },
    resetData() {
      addedMembers.value = [];
      addedMembersSet.clear();
      originAddedMembersSet.clear();
    },
  };

  return {
    addedMembers,
    addedMembersSet,
    originAddedMembersSet,
    methods,
  };
}
